import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FaTimes, FaSave, FaSyncAlt } from 'react-icons/fa';
import { ui } from '../utils/styles';

const DividaModal = ({ show, onClose, divida, onSave, mode, devedor, clientesContratantes }) => {

    // --- Estados Locais do Formulário ---
    const [valorOriginal, setValorOriginal] = useState('');
    const [dataVencimento, setDataVencimento] = useState('');
    const [status, setStatus] = useState('Em Aberto');
    const [observacao, setObservacao] = useState('');
    const [idProcessoJudicial, setIdProcessoJudicial] = useState('');

    // NOVO: Estado para o Cliente (Contratante) dono desta dívida
    const [clienteContratanteId, setClienteContratanteId] = useState('');

    const [loading, setLoading] = useState(false);

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const authHeader = useMemo(
        () => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }),
        []
    );

    const statusOptions = ['Em Aberto', 'Vencido', 'Em Acordo', 'Pago', 'Cancelado'];

    // --- Efeitos de Inicialização ---
    useEffect(() => {
        if (show) {
            if (divida && (mode === 'edit' || mode === 'view')) {
                setValorOriginal(divida.valor_original.toString().replace(/\D/g, '') || '');
                setDataVencimento(divida.vencimento ? divida.vencimento.split('T')[0] : '');
                setStatus(divida.status || 'Em Aberto');
                setObservacao(divida.obs || '');
                setIdProcessoJudicial(divida.id_processo_judicial || '');
                setClienteContratanteId(divida.id_cliente || '');
            } else {
                setValorOriginal('0');
                setDataVencimento('');
                setStatus('Em Aberto');
                setObservacao('');
                setIdProcessoJudicial('');

                // Tenta pegar o contratante padrão do localStorage ou deixa vazio
                const storedEmitenteId = localStorage.getItem('userIdEmitente');
                setClienteContratanteId(storedEmitenteId || '');
            }
        }
    }, [show, divida, mode]);

    // --- Funções Auxiliares ---
    const formatCurrencyDisplay = (value) => {
        const cleaned = value.toString().replace(/\D/g, '');
        if (!cleaned) return '';
        const num = Number(cleaned) / 100;
        return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleValorChange = (e) => {
        const cleaned = e.target.value.toString().replace(/\D/g, '');
        setValorOriginal(cleaned);
    };

    // --- Lógica de Salvamento ---
    const handleSave = async (e) => {
        e.preventDefault();

        if (!valorOriginal || !dataVencimento || !clienteContratanteId) {
            toast.error('Preencha o Valor, Vencimento e o Cliente Contratante.');
            return;
        }

        let valorParaBackend = Number(valorOriginal) / 100;

        const payload = {
            id_devedor: devedor?.id || divida?.id_devedor,
            id_cliente: Number(clienteContratanteId), // Agora fixado pela escolha no modal
            valor_original: valorParaBackend,
            valor_saldo: valorParaBackend,
            data_vencimento: dataVencimento,
            status,
            observacao,
            id_processo_judicial: idProcessoJudicial || 0
        };

        setLoading(true);
        const toastId = toast.loading(mode === 'create' ? 'Lançando Dívida...' : 'Atualizando...');

        try {
            if (mode === 'create') {
                await axios.post(`${URL_BACKEND}/divida`, payload, { headers: authHeader });
                toast.success('Dívida lançada com sucesso!', { id: toastId });
            } else if (mode === 'edit') {
                await axios.put(`${URL_BACKEND}/divida/${divida.id}`, payload, { headers: authHeader });
                toast.success('Dívida atualizada!', { id: toastId });
            }

            onSave();
            onClose();
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao salvar.';
            toast.error(msg, { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;
    const isViewMode = mode === 'view';

    return (
        <div style={ui.modalOverlay}>
            <div style={ui.modalContent}>
                <div style={ui.modalHeader}>
                    <h2 style={ui.modalTitle}>
                        {mode === 'create' ? 'Lançar Nova Dívida' : mode === 'edit' ? `Editar Dívida #${divida?.id}` : `Visualizar Dívida #${divida?.id}`}
                    </h2>
                    <button onClick={onClose} style={ui.modalCloseButton} disabled={loading}><FaTimes /></button>
                </div>

                <form onSubmit={handleSave} style={ui.modalBody}>
                    <div style={{ marginBottom: 16, padding: '10px', background: '#f3f4f6', borderRadius: '8px' }}>
                        <p style={{ fontSize: '0.9rem', color: '#374151', margin: 0 }}>
                            <strong>Devedor:</strong> {devedor?.nome || 'Não selecionado'}
                        </p>
                    </div>

                    <div style={ui.formGrid}>

                        {/* 1. SELEÇÃO DO CONTRATANTE (Obrigatório) */}
                        <div style={{ ...ui.formGroup, gridColumn: 'span 2' }}>
                            <label style={ui.label}>Cliente Contratante (Credor) *</label>
                            <select
                                style={ui.select}
                                value={clienteContratanteId}
                                onChange={e => setClienteContratanteId(e.target.value)}
                                disabled={isViewMode || loading}
                                required
                            >
                                <option value="">Selecione quem é o credor...</option>
                                {clientesContratantes?.map(c => (
                                    <option key={c.id} value={String(c.id)}>{c.nome}</option>
                                ))}
                            </select>
                        </div>

                        {/* 2. Valor Original */}
                        <div style={ui.formGroup}>
                            <label style={ui.label}>Valor Original (R$) *</label>
                            <input
                                style={ui.input}
                                value={formatCurrencyDisplay(valorOriginal)}
                                onChange={handleValorChange}
                                disabled={isViewMode || loading}
                                inputMode="numeric"
                                required
                            />
                        </div>

                        {/* 3. Data de Vencimento */}
                        <div style={ui.formGroup}>
                            <label style={ui.label}>Data de Vencimento *</label>
                            <input
                                type="date"
                                style={ui.input}
                                value={dataVencimento}
                                onChange={e => setDataVencimento(e.target.value)}
                                disabled={isViewMode || loading}
                                required
                            />
                        </div>

                        {/* 4. Status */}
                        <div style={ui.formGroup}>
                            <label style={ui.label}>Status</label>
                            <select
                                style={ui.select}
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                                disabled={isViewMode || loading}
                            >
                                {statusOptions.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>

                        {/* 5. Processo Judicial */}
                        <div style={ui.formGroup}>
                            <label style={ui.label}>ID Processo (Opcional)</label>
                            <input
                                type="number"
                                style={ui.input}
                                value={idProcessoJudicial}
                                onChange={e => setIdProcessoJudicial(e.target.value)}
                                disabled={isViewMode || loading}
                                placeholder="Ex: 12345"
                            />
                        </div>

                        {/* 6. Observação */}
                        <div style={{ ...ui.formGroup, gridColumn: 'span 2' }}>
                            <label style={ui.label}>Observação</label>
                            <textarea
                                style={{ ...ui.input, minHeight: 60, resize: 'vertical' }}
                                value={observacao}
                                onChange={e => setObservacao(e.target.value)}
                                disabled={isViewMode || loading}
                            />
                        </div>
                    </div>

                    <div style={ui.modalFooter}>
                        <button type="button" onClick={onClose} style={ui.buttonLight} disabled={loading}>
                            {isViewMode ? 'Fechar' : 'Cancelar'}
                        </button>

                        {(mode === 'create' || mode === 'edit') && (
                            <button type="submit" style={{ ...ui.buttonPrimary, background: '#10b981' }} disabled={loading}>
                                {loading ? <FaSyncAlt className="fa-spin" /> : <FaSave />}
                                {mode === 'create' ? ' Lançar Dívida' : ' Salvar Alterações'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DividaModal;